import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import Loader from "react-loader-spinner";
import { FiArrowLeft, FiRotateCcw, FiAlertCircle, FiPlus, FiTrash2, FiX } from "react-icons/fi";
import BottomModal from '../components/BottomModal';
import {
    fetchAdminLeagueStructure,
    fetchLeagueTeams,
    fetchLeagueTeamsByGroups,
    fetchAdminTeamsList,
    fetchAllSeasons,
    fetchApplicationTemplates,
    fetchLeaguePartners,
    updateLeague,
    updateLeagueSettings,
    createLeagueSeason,
    setSeasonTeams,
    setSeasonGroups,
    createApplicationTemplate,
    fetchAdminClientImages,
} from '../actions/leaguesActions';
import { BASE_URL } from '../constants/config';
import { buildLeagueStructure, leagueLogoCacheBust } from '../utilities/leagueStructure';
import {
    settingsFormFrom,
    settingsFormToPatch,
    isSettingsFormValid,
} from '../utilities/seasonSettingsForm';
import SeasonSettingsFields from '../components/SeasonSettingsFields';
import SeasonRoster from '../components/SeasonRoster';
import SaveBar from '../components/SaveBar';
import CreateSeasonModal from '../components/CreateSeasonModal';
import DrawCeremonyModal from '../components/DrawCeremonyModal';
import LeaguePartnersPanel from '../components/LeaguePartnersPanel';
import { leagueGroupsFrom } from '../utilities/partners';
import '../styles/leagueManagement.css';

const MAX_LOGO_BYTES = 1024 * 1024;

const resolveImage = (image, bust) => {
    if (!image) return null;
    const url = image.startsWith('http') || image.startsWith('data:')
        ? image
        : BASE_URL.url + image;
    if (!bust || image.startsWith('data:')) return url;
    return `${url}${url.includes('?') ? '&' : '?'}t=${bust}`;
};

const normalizeTeam = (team) => ({
    team_id: Number(team.team_id !== undefined ? team.team_id : team.id),
    name: team.name || team.team_name || 'Без названия',
    image: team.image || '',
    short_name: team.short_name || '',
});

// Из ответа teamsByGroups собираем список групп и привязку команд.
// Сервер отдаёт только group_name, поэтому id генерируем локально —
// так же, как это делает League.get_groups (models/leagues.js:2316).
const deriveGroupsFromServer = (serverGroupTeams) => {
    const list = [];
    const assignment = {};
    const idByName = {};
    (serverGroupTeams || []).forEach(row => {
        const name = row.group_name;
        if (!name) return;
        if (idByName[name] === undefined) {
            idByName[name] = list.length + 1;
            list.push({ id: idByName[name], name, priority: list.length + 1 });
        }
        assignment[Number(row.team_id)] = idByName[name];
    });
    return { list, assignment };
};

const leagueFormFrom = (league) => ({
    name: league.name || '',
    championat_id: league.championat_id !== null ? String(league.championat_id) : '',
    image: league.image || '',
    priority: String(league.priority !== undefined && league.priority !== null ? league.priority : 1),
    active_season: league.active_season !== null ? String(league.active_season) : '',
    enabled: Number(league.enabled) !== 0,
});

export default function ManageLeague() {
    const { leagueId } = useParams();
    const history = useHistory();
    const dispatch = useDispatch();

    const [structurePayload, setStructurePayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [activeTab, setActiveTab] = useState('league');
    const [selectedSeason, setSelectedSeason] = useState(null);

    const [leagueForm, setLeagueForm] = useState(null);
    const [settingsForm, setSettingsForm] = useState(null);
    const [logoError, setLogoError] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [logoObjectUrl, setLogoObjectUrl] = useState('');
    const fileInputRef = useRef(null);

    const [serverTeams, setServerTeams] = useState([]);
    const [serverGroupTeams, setServerGroupTeams] = useState([]);
    const [rosterLoading, setRosterLoading] = useState(false);

    const [allTeams, setAllTeams] = useState([]);
    const [allTeamsLoading, setAllTeamsLoading] = useState(false);
    const [createSeasonOpen, setCreateSeasonOpen] = useState(false);
    const [ceremonyOpen, setCeremonyOpen] = useState(false);
    // Сеяных отмечают прямо перед жеребьёвкой, поэтому просто передаём их в церемонию
    const [ceremonySeeded, setCeremonySeeded] = useState([]);

    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState({ text: '', type: 'success', visible: false, id: 0 });

    const [allSeasons, setAllSeasons] = useState([]);
    const [applications, setApplications] = useState([]);
    const [partners, setPartners] = useState([]);

    // Черновики состава и групп: запрос уходит только по кнопке "Сохранить"
    const [rosterDraft, setRosterDraft] = useState(null);
    const [groupsDraft, setGroupsDraft] = useState(null);
    const selectedSeasonRef = useRef(selectedSeason);
    selectedSeasonRef.current = selectedSeason;

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const payload = await dispatch(fetchAdminLeagueStructure());
            setStructurePayload(payload || { championats: [], orphan_leagues: [] });
        } catch (e) {
            console.error('Ошибка загрузки лиги:', e);
            setError(e);
        } finally {
            setLoading(false);
        }
    }, [dispatch]);

    useEffect(() => { loadData(); }, [loadData]);

    // Справочник команд клиента грузим один раз — он нужен и для добавления
    // команды в состав, и для мастера создания сезона
    useEffect(() => {
        let cancelled = false;
        setAllTeamsLoading(true);
        dispatch(fetchAdminTeamsList())
            .then(data => { if (!cancelled) setAllTeams(Array.isArray(data) ? data : []); })
            .catch(e => {
                if (cancelled) return;
                console.error('Ошибка загрузки справочника команд:', e);
                setAllTeams([]);
            })
            .finally(() => { if (!cancelled) setAllTeamsLoading(false); });
        return () => { cancelled = true; };
    }, [dispatch]);


    // Справочники сезонов и заявок клиента
    useEffect(() => {
        let cancelled = false;
        dispatch(fetchAllSeasons())
            .then(data => {
                if (cancelled) return;
                setAllSeasons((Array.isArray(data) ? data : []).map(season => ({
                    season_id: Number(season.id !== undefined ? season.id : season.season_id),
                    season_name: season.name || season.season_name,
                })));
            })
            .catch(e => {
                if (!cancelled) console.error('Ошибка загрузки справочника сезонов:', e);
            });
        dispatch(fetchApplicationTemplates())
            .then(data => { 
                if (!cancelled) setApplications(Array.isArray(data) ? data : []); })
            .catch(e => {
                if (!cancelled) console.error('Ошибка загрузки справочника заявок:', e);
            });
        return () => { cancelled = true; };
    }, [dispatch]);

    const { championats, league, seasonsCatalog, allLeagues, partnerGroups } = useMemo(() => {
        const structure = buildLeagueStructure(structurePayload);
        return {
            championats: structure.championats,
            seasonsCatalog: structure.seasonsCatalog,
            allLeagues: structure.leagues,
            league: structure.leaguesById[Number(leagueId)] || null,
            partnerGroups: leagueGroupsFrom(structure),
        };
    }, [structurePayload, leagueId]);

    const [clientImages, setClientImages] = useState({});
    useEffect(() => {
        dispatch(fetchAdminClientImages()).then(map => setClientImages(map || {})).catch(() => {});
    }, [dispatch]);

    useEffect(() => {
        if (!league) return;
        setLeagueForm(leagueFormFrom(league));
        setLogoFile(null);
        setLogoError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setSelectedSeason(prev => {
            if (prev !== null && league.settings[prev]) return prev;
            if (league.active_season !== null && league.settings[league.active_season]) return league.active_season;
            return league.seasons.length ? league.seasons[0].season_id : null;
        });
    }, [league]);

    useEffect(() => {
        if (!logoFile) {
            setLogoObjectUrl('');
            return undefined;
        }
        const url = URL.createObjectURL(logoFile);
        setLogoObjectUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [logoFile]);

    const seasonSettings = league && selectedSeason !== null ? league.settings[selectedSeason] : null;
    const seasonLeagueType = seasonSettings ? Number(seasonSettings.league_type) : 1;

    useEffect(() => {
        if (!seasonSettings) {
            setSettingsForm(null);
            return;
        }
        setSettingsForm(settingsFormFrom(seasonSettings));
    }, [seasonSettings]);

    // Блок групп показываем по типу из формы, а не из сохранённых настроек:
    // переключил на "обычный чемпионат" — группы скрылись сразу.
    // Загрузку групп с сервера при этом дёргаем по сохранённому типу,
    // чтобы переключатель не слал лишних запросов.
    const formLeagueType = settingsForm ? Number(settingsForm.league_type) : seasonLeagueType;

    // Состав сезона и группы
    useEffect(() => {
        if (selectedSeason === null) {
            setServerTeams([]);
            setServerGroupTeams([]);
            return;
        }
        let cancelled = false;
        setRosterLoading(true);
        const requests = [dispatch(fetchLeagueTeams(leagueId, selectedSeason)).catch(e => {
            console.error('Ошибка загрузки команд сезона:', e);
            return [];
        })];
        requests.push(
            seasonLeagueType === 2
                ? dispatch(fetchLeagueTeamsByGroups(leagueId, selectedSeason)).catch(e => {
                    console.error('Ошибка загрузки групп сезона:', e);
                    return [];
                })
                : Promise.resolve([])
        );
        Promise.all(requests)
            .then(([teamsData, groupsData]) => {
                if (cancelled) return;
                setServerTeams(Array.isArray(teamsData) ? teamsData : []);
                setServerGroupTeams(Array.isArray(groupsData) ? groupsData : []);
            })
            .finally(() => { if (!cancelled) setRosterLoading(false); });
        return () => { cancelled = true; };
    }, [dispatch, leagueId, selectedSeason, seasonLeagueType]);


    // Справочник команд нормализуем один раз: у клиента их бывает больше тысячи,
    // и пересчитывать это на каждое нажатие в поиске незачем
    const teamCatalog = useMemo(() => allTeams.map(team => {
        const name = team.name || team.team_name || 'Без названия';
        return {
            team_id: Number(team.team_id !== undefined ? team.team_id : team.id),
            name,
            image: team.image || '',
            short_name: team.short_name || '',
            searchName: name.toLowerCase(),
        };
    }), [allTeams]);

    // Спонсоры лиги для заставки жеребьёвки
    useEffect(() => {
        if (selectedSeason === null) {
            setPartners([]);
            return undefined;
        }
        let cancelled = false;
        dispatch(fetchLeaguePartners(leagueId, selectedSeason))
            .then(data => { if (!cancelled) setPartners(Array.isArray(data) ? data : []); })
            .catch(() => { if (!cancelled) setPartners([]); });
        return () => { cancelled = true; };
    }, [dispatch, leagueId, selectedSeason]);

    // Сохранённое состояние состава — то, что пришло с сервера
    const savedRoster = useMemo(
        () => serverTeams.map(normalizeTeam),
        [serverTeams]
    );

    const savedGroups = useMemo(
        () => deriveGroupsFromServer(serverGroupTeams),
        [serverGroupTeams]
    );

    // Черновики сбрасываются по отдельности: состав и группы сохраняются
    // разными кнопками, и успех одной не должен откатывать другую.
    useEffect(() => {
        setRosterDraft(savedRoster);
    }, [savedRoster]);

    useEffect(() => {
        setGroupsDraft(savedGroups);
    }, [savedGroups]);

    const roster = rosterDraft !== null ? rosterDraft : savedRoster;
    const groupsState = groupsDraft !== null ? groupsDraft : savedGroups;

    // Состав и группы — разные таблицы на бэкенде, поэтому и отслеживаем,
    // и сохраняем их по отдельности. На сервер уходят только id команд,
    // лишние поля справочника (searchName и т.п.) dirty не делают.
    const rosterDirty = useMemo(() => {
        if (roster.length !== savedRoster.length) return true;
        return roster.some((team, i) => team.team_id !== savedRoster[i].team_id);
    }, [roster, savedRoster]);

    const groupsDirty = useMemo(
        () => JSON.stringify(groupsState) !== JSON.stringify(savedGroups),
        [groupsState, savedGroups]
    );

    const showToast = (text, type = 'success') => {
        setToast({ text, type, visible: true, id: Date.now() });
    };

    useEffect(() => {
        if (!toast.visible) return undefined;
        const timer = setTimeout(() => {
            setToast(prev => (prev.id === toast.id ? { ...prev, visible: false } : prev));
        }, 5000);
        return () => clearTimeout(timer);
    }, [toast.id, toast.visible]);

    // Любое сохранение уходит на сервер. Структуру лиги перечитываем только
    // когда она реально могла измениться — состав и группы в ней не живут.
    const runSave = async (request, successMessage, { refreshStructure = true } = {}) => {
        setSaving(true);
        try {
            const result = await dispatch(request);
            if (refreshStructure) await loadData();
            if (successMessage) showToast(successMessage, 'success');
            return result == null ? true : result;
        } catch (e) {
            console.error('Ошибка сохранения:', e);
            const message = (e.response && e.response.data)
                || e.message
                || 'Не удалось сохранить изменения';
            showToast(typeof message === 'string' ? message : 'Не удалось сохранить изменения', 'error');
            return null;
        } finally {
            setSaving(false);
        }
    };

    const setLeagueField = (field, value) => setLeagueForm(prev => ({ ...prev, [field]: value }));
    const setSettingsField = (field, value) => setSettingsForm(prev => ({ ...prev, [field]: value }));

    const pickLogo = (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setLogoError('Нужен файл изображения.');
            return;
        }
        if (file.size > MAX_LOGO_BYTES) {
            setLogoError('Файл больше 1 МБ — выберите поменьше.');
            return;
        }
        setLogoError(null);
        setLogoFile(file);
    };

    const clearLogo = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setLogoFile(null);
        setLogoError(null);
        setLeagueField('image', '');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const resetLeagueForm = () => {
        setLeagueForm(leagueFormFrom(league));
        setLogoFile(null);
        setLogoError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const leagueDirty = useMemo(() => {
        if (!league || !leagueForm) return false;
        if (logoFile) return true;
        return JSON.stringify(leagueForm) !== JSON.stringify(leagueFormFrom(league));
    }, [league, leagueForm, logoFile]);

    const settingsDirty = useMemo(() => {
        if (!seasonSettings || !settingsForm) return false;
        return JSON.stringify(settingsForm) !== JSON.stringify(settingsFormFrom(seasonSettings));
    }, [seasonSettings, settingsForm]);

    const leaguePriority = leagueForm ? parseInt(leagueForm.priority, 10) : NaN;
    const leagueValid = leagueForm
        && leagueForm.name.trim().length > 0
        && leagueForm.championat_id !== ''
        && !isNaN(leaguePriority) && leaguePriority >= 0;

    const saveLeague = () => {
        if (!leagueValid) return;
        return runSave(updateLeague({
            league_id: league.id,
            name: leagueForm.name.trim(),
            championat_id: parseInt(leagueForm.championat_id, 10),
            image: leagueForm.image || '',
            logoFile,
            priority: leaguePriority,
            active_season: leagueForm.active_season === '' ? null : parseInt(leagueForm.active_season, 10),
            enabled: leagueForm.enabled ? 1 : 0,
        }), 'Данные лиги сохранены');
    };

    const saveSettings = () => {
        if (!isSettingsFormValid(settingsForm)) return;
        return runSave(updateLeagueSettings({
            league_id: league.id,
            season_id: selectedSeason,
            ...settingsFormToPatch(settingsForm),
        }), 'Настройки сезона сохранены');
    };

    const saveRoster = async () => {
        const seasonId = selectedSeason;
        const snapshot = roster.map(normalizeTeam);
        const result = await runSave(setSeasonTeams({
            league_id: league.id,
            season_id: seasonId,
            teams: snapshot.map(t => t.team_id),
        }), 'Состав сохранён', { refreshStructure: false });
        // Структура лиги состав не содержит, поэтому baseline обновляем локально —
        // иначе rosterDirty сравнивает черновик с устаревшим serverTeams.
        if (result !== null && selectedSeasonRef.current === seasonId) {
            setServerTeams(snapshot);
        }
    };

    const saveGroups = async () => {
        const seasonId = selectedSeason;
        const snapshot = groupsState;
        const result = await runSave(setSeasonGroups({
            league_id: league.id,
            season_id: seasonId,
            // Обычные группы живут только в league_groups и различаются по имени.
            // group_id не шлём: league_groups_ex заполняется вручную для особых случаев
            groups: snapshot.list.map(group => ({
                group_name: group.name,
                priority: group.priority,
                teams: roster
                    .filter(t => snapshot.assignment[t.team_id] === group.id)
                    .map(t => t.team_id),
            })),
        }), 'Группы сохранены', { refreshStructure: false });
        if (result !== null && selectedSeasonRef.current === seasonId) {
            const rows = [];
            snapshot.list.forEach(group => {
                roster.forEach(team => {
                    if (snapshot.assignment[team.team_id] === group.id) {
                        rows.push({ team_id: team.team_id, group_name: group.name });
                    }
                });
            });
            setServerGroupTeams(rows);
        }
    };

    const resetRoster = () => setRosterDraft(savedRoster);
    const resetGroups = () => setGroupsDraft(savedGroups);

    const handleCreateApplication = async (application) => {
        const created = await runSave(
            createApplicationTemplate(application),
            'Заявка создана'
        );
        if (created && created.id) {
            setApplications(prev => [...prev, created]);
        }
        return created;
    };

    // Для копирования состава в мастере создания сезона.
    // Лига может быть любой из доступных админу, не только текущая: у только что
    // созданной лиги своей истории нет, а состав обычно берут из соседней.
    const resolveRoster = useCallback(async (sourceLeagueId, sourceSeasonId) => {
        const data = await dispatch(fetchLeagueTeams(sourceLeagueId, sourceSeasonId));
        return Array.isArray(data) ? data : [];
    }, [dispatch]);

    const handleCreateSeason = async (season, settingsPatch, teams) => {
        const created = await runSave(createLeagueSeason({
            league_id: league.id,
            season_id: season.season_id,
            ...settingsPatch,
            teams: (teams || []).map(t => t.team_id),
        }), `Сезон ${season.season_name} создан`);
        if (created !== null) {
            setSelectedSeason(Number(season.season_id));
            setActiveTab('season');
        }
    };

    if (loading) {
        return (
            <div className="layoutWrapper">
                <div className="matchCenterWrapper">
                    <div className="loaderWrapper">
                        <Loader type="Oval" color="#4A5568" height={80} width={80} />
                        <p style={{ marginTop: '20px', color: '#718096' }}>Загрузка лиги...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !league) {
        return (
            <div className="layoutWrapper">
                <div className="matchCenterWrapper">
                    <div className="matchCenterHeader">
                        <button className="lmBackButton" onClick={() => history.push('/leagues_management')}>
                            <FiArrowLeft /> К списку лиг
                        </button>
                    </div>
                    <div className="errorMessage">
                        <p>
                            {error
                                ? `Ошибка загрузки: ${(error.response && error.response.data) || error.message}`
                                : `Лига с ID ${leagueId} не найдена или недоступна`}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    const championat = championats.find(ch => ch.id === league.championat_id);
    const currentSeason = league.seasons.find(s => s.season_id === selectedSeason);
    const logoBust = leagueLogoCacheBust(league.id);
    const logoPreview = logoObjectUrl || resolveImage(leagueForm && leagueForm.image, logoBust);

    // Основной логотип — clients.image клиента выбранного чемпионата
    const formChampionat = leagueForm
        ? championats.find(ch => String(ch.id) === String(leagueForm.championat_id))
        : null;
    const clientLogo = (formChampionat && (formChampionat.client_image || clientImages[formChampionat.client_id]))
        || clientImages[league.client_id]
        || '';
    const usingClientLogo = Boolean(clientLogo) && !logoFile && Boolean(leagueForm) && leagueForm.image === clientLogo;
    // Снятие галочки возвращает прежний логотип лиги, если он был своим
    const toggleClientLogo = () => {
        if (usingClientLogo) {
            setLeagueField('image', league.image && league.image !== clientLogo ? league.image : '');
            return;
        }
        setLogoFile(null);
        setLogoError(null);
        setLeagueField('image', clientLogo);
    };
    return (
        <div className="layoutWrapper">
            <div className="matchCenterWrapper">
                <div className="leagueDetailHeader">
                    <button className="lmBackButton" onClick={() => history.push('/leagues_management')}>
                        <FiArrowLeft /> К списку лиг
                    </button>
                    <div className="leagueIdentity">
                        <div className="leagueIdentityLogo">
                            {resolveImage(league.image, logoBust) ? (
                                <img
                                    key={logoBust || league.image}
                                    src={resolveImage(league.image, logoBust)}
                                    alt={league.name}
                                />
                            ) : (
                                <span>{(league.name || '?').charAt(0).toUpperCase()}</span>
                            )}
                        </div>
                        <div className="leagueIdentityText">
                            <h1>{league.name}</h1>
                            <p>
                                {championat ? championat.name : 'Без чемпионата'}
                                <span className="dotSep">•</span>
                                {league.enabled ? 'видна в приложении' : 'скрыта в приложении'}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="tabsBar">
                    <button
                        className={`tabButton ${activeTab === 'league' ? 'tabButtonActive' : ''}`}
                        onClick={() => setActiveTab('league')}
                    >
                        Лига
                    </button>
                    <button
                        className={`tabButton ${activeTab === 'season' ? 'tabButtonActive' : ''}`}
                        onClick={() => setActiveTab('season')}
                    >
                        Сезоны
                    </button>
                    <button
                        className={`tabButton ${activeTab === 'sponsors' ? 'tabButtonActive' : ''}`}
                        onClick={() => setActiveTab('sponsors')}
                    >
                        Спонсоры
                    </button>
                </div>

                {activeTab === 'sponsors' && (
                    <div className="tabPanel">
                        <LeaguePartnersPanel league={league} groups={partnerGroups} onToast={showToast} />
                    </div>
                )}

                {activeTab === 'league' && leagueForm && (
                    <div className="tabPanel">
                        <div className="settingsCard">
                            <div className="settingsCardHeader">
                                <h2>Основные данные</h2>
                                <p>Название и место лиги в структуре. Общее для всех сезонов.</p>
                            </div>
                            <div className="settingsCardBody">
                                <div className="lmRow">
                                    <label className="modalLabel">Название лиги</label>
                                    <input
                                        type="text"
                                        className="modalInput"
                                        value={leagueForm.name}
                                        maxLength={100}
                                        onChange={(e) => setLeagueField('name', e.target.value)}
                                    />
                                    {!leagueForm.name.trim() && (
                                        <span className="fieldError">Название не может быть пустым.</span>
                                    )}
                                </div>

                                <div className="lmRow">
                                    <label className="modalLabel">Чемпионат</label>
                                    <select
                                        className="modalInput"
                                        value={leagueForm.championat_id}
                                        onChange={(e) => setLeagueField('championat_id', e.target.value)}
                                    >
                                        <option value="">— не выбран —</option>
                                        {championats.map(ch => (
                                            <option key={ch.id} value={ch.id}>{ch.name}</option>
                                        ))}
                                    </select>
                                    <span className="fieldHint">Лига переедет в выбранный чемпионат.</span>
                                </div>

                                <div className="lmRow">
                                    <label className="modalLabel">Логотип</label>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        className="logoSquareInput"
                                        onChange={pickLogo}
                                    />
                                    {logoPreview ? (
                                        <div className="logoSquare logoSquareFilled">
                                            <img
                                                key={logoBust || logoPreview}
                                                src={logoPreview}
                                                alt={leagueForm.name}
                                            />
                                            <button
                                                type="button"
                                                className="logoSquareHit"
                                                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                            >
                                                Сменить
                                            </button>
                                            <button
                                                type="button"
                                                className="logoSquareRemove"
                                                aria-label="Убрать логотип"
                                                onClick={clearLogo}
                                            >
                                                <FiX />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            className="logoSquare"
                                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                        >
                                            <FiPlus />
                                            <span>Добавить логотип</span>
                                        </button>
                                    )}
                                    {logoError && <span className="fieldError">{logoError}</span>}
                                    <span className="fieldHint">PNG или JPG до 1 МБ.</span>
                                    {clientLogo && (
                                        <label className="ptClientLogo">
                                            <input
                                                type="checkbox"
                                                checked={usingClientLogo}
                                                onChange={toggleClientLogo}
                                            />
                                            <img src={resolveImage(clientLogo)} alt="" />
                                            Использовать основной лого
                                        </label>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="settingsCard">
                            <div className="settingsCardHeader">
                                <h2>Базовые настройки</h2>
                                <p>Как лига показывается в приложении.</p>
                            </div>
                            <div className="settingsCardBody">
                                <div className="lmRow">
                                    <label className="modalLabel">Активный сезон</label>
                                    {league.seasons.length === 0 ? (
                                        <>
                                            <button
                                                type="button"
                                                className="readonlyValue readonlyMuted readonlyValueClickable"
                                                onClick={() => setActiveTab('season')}
                                            >
                                                Нет созданных сезонов
                                            </button>
                                            <span className="fieldHint">
                                                Необходимо создать хотя бы один сезон и выставить его активным,
                                                чтобы лига корректно отображалась в приложении.
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <select
                                                className="modalInput"
                                                value={leagueForm.active_season}
                                                onChange={(e) => setLeagueField('active_season', e.target.value)}
                                            >
                                                <option value="">— не выбран —</option>
                                                {league.seasons.map(s => (
                                                    <option key={s.season_id} value={s.season_id}>{s.season_name}</option>
                                                ))}
                                            </select>
                                            <span className="fieldHint">
                                                Сезон, который открывается в приложении по умолчанию.
                                            </span>
                                        </>
                                    )}
                                </div>

                                <div className="lmRow">
                                    <label className="modalLabel">Приоритет</label>
                                    <input
                                        type="number"
                                        className="modalInput inputNarrow"
                                        min={0}
                                        value={leagueForm.priority}
                                        onChange={(e) => setLeagueField('priority', e.target.value)}
                                    />
                                    <span className="fieldHint">Чем меньше число, тем выше лига в списке.</span>
                                    {(isNaN(leaguePriority) || leaguePriority < 0) && (
                                        <span className="fieldError">Введите целое число от 0.</span>
                                    )}
                                </div>

                                <div className="lmRow">
                                    <div className="switchRow">
                                        <div className="switchText">
                                            <span className="switchTitle">Видна в приложении</span>
                                            <span className="fieldHint">
                                                Выключенная лига остаётся в базе, но скрыта от пользователей.
                                            </span>
                                        </div>
                                        <label className="switch">
                                            <input
                                                type="checkbox"
                                                checked={leagueForm.enabled}
                                                onChange={(e) => setLeagueField('enabled', e.target.checked)}
                                            />
                                            <span className="switchSlider" />
                                        </label>
                                    </div>
                                </div>

                            </div>
                        </div>

                        <div className="saveBar">
                            <button
                                className="modalButton modalButtonCancel"
                                onClick={resetLeagueForm}
                                disabled={!leagueDirty || saving}
                            >
                                <FiRotateCcw style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                Отменить изменения
                            </button>
                            <button
                                className="modalButton modalButtonSave"
                                onClick={saveLeague}
                                disabled={!leagueDirty || !leagueValid || saving}
                            >
                                Сохранить
                            </button>
                        </div>
                    </div>
                )}

                {activeTab === 'season' && (
                    <div className="tabPanel">
                        <div className="seasonPicker">
                            <span className="seasonPickerLabel">Сезон:</span>
                            <div className="seasonPills">
                                {league.seasons.map(s => (
                                    <button
                                        key={s.season_id}
                                        className={`seasonPill ${selectedSeason === s.season_id ? 'seasonPillActive' : ''}`}
                                        onClick={() => setSelectedSeason(s.season_id)}
                                    >
                                        {s.season_name}
                                        {league.active_season === s.season_id && (
                                            <span className="seasonPillBadge">активный</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                            <button
                                className="actionButtonSolid"
                                onClick={() => setCreateSeasonOpen(true)}
                            >
                                <FiPlus style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                Добавить сезон
                            </button>
                        </div>

                        {league.seasons.length === 0 ? (
                            <div className="emptyState">
                                <p>У лиги нет сезонов. Создайте первый кнопкой «Добавить сезон».</p>
                            </div>
                        ) : selectedSeason === null || !settingsForm || !seasonSettings ? (
                            <div className="emptyState">
                                <p>Выберите сезон.</p>
                            </div>
                        ) : (
                            <>
                                <div className="seasonScopeNote">
                                    <FiAlertCircle style={{ marginRight: '8px', flexShrink: 0 }} />
                                    <span>
                                        Настройки и состав ниже относятся только к сезону&nbsp;
                                        <strong>{currentSeason && currentSeason.season_name}</strong>.
                                        В других сезонах они могут отличаться.
                                    </span>
                                </div>

                                <div className="seasonColumns">
                                    <div className="seasonBlock">
                                        <div className="seasonBlockHead">
                                            <div>
                                                <h2>Настройки сезона</h2>
                                                <p>
                                                    Формат, рейтинг и заявка. Сохраняются отдельно
                                                    от состава команд рядом.
                                                </p>
                                            </div>
                                            {settingsDirty && (
                                                <span className="seasonBlockDirty">не сохранено</span>
                                            )}
                                        </div>
                                        <SeasonSettingsFields
                                            form={settingsForm}
                                            onChange={setSettingsField}
                                            applications={applications}
                                            onCreateApplication={handleCreateApplication}
                                        />
                                        <SaveBar
                                            dirty={settingsDirty}
                                            onSave={saveSettings}
                                            onReset={() => setSettingsForm(settingsFormFrom(seasonSettings))}
                                            label="Сохранить настройки"
                                            title="Все поля в этом блоке"
                                            hint="Настройки изменены и не сохранены"
                                            saveDisabled={saving || !isSettingsFormValid(settingsForm)}
                                        />
                                    </div>

                                    <SeasonRoster
                                        leagueType={formLeagueType}
                                        teams={roster}
                                        onTeamsChange={setRosterDraft}
                                        groupsState={groupsState}
                                        onGroupsChange={setGroupsDraft}
                                        allTeams={teamCatalog}
                                        allTeamsLoading={allTeamsLoading}
                                        loading={rosterLoading}
                                        onOpenTeam={(teamId) => history.push(`/teams_management/${teamId}`)}
                                        rosterDirty={rosterDirty}
                                        onSaveRoster={saveRoster}
                                        onResetRoster={resetRoster}
                                        groupsDirty={groupsDirty}
                                        onSaveGroups={saveGroups}
                                        onResetGroups={resetGroups}
                                        onOpenCeremony={(seeded) => {
                                            setCeremonySeeded(seeded || []);
                                            setCeremonyOpen(true);
                                        }}
                                        saving={saving}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            <CreateSeasonModal
                isOpen={createSeasonOpen}
                onClose={() => setCreateSeasonOpen(false)}
                onCreate={handleCreateSeason}
                league={league}
                seasonsCatalog={allSeasons.length > 0 ? allSeasons : seasonsCatalog}
                resolveRoster={resolveRoster}
                allLeagues={allLeagues}
                allTeams={teamCatalog}
                allTeamsLoading={allTeamsLoading}
                applications={applications}
                onCreateApplication={handleCreateApplication}
            />

            <DrawCeremonyModal
                isOpen={ceremonyOpen}
                onClose={() => setCeremonyOpen(false)}
                onApply={(assignment) => setGroupsDraft({ ...groupsState, assignment })}
                teams={roster}
                groups={groupsState.list}
                leagueName={league.name}
                leagueImage={league.image}
                seasonName={(currentSeason && currentSeason.season_name) || ''}
                sponsors={partners}
                seededIds={ceremonySeeded}
            />

            <div className={`matchResultsModalWrapper${toast.visible ? ' matchResultsModalWrapperActive' : ''}`}>
                <BottomModal text={toast.text} type={toast.type} />
            </div>
        </div>
    );
}
